import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgbModule, NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxWebstorageModule } from 'ngx-webstorage';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './shared/shared.module';
import { CommonModule, HashLocationStrategy, LocationStrategy } from '@angular/common';
// import { UserSettingsDashboard } from './user_settings/user_settings_dashboard/user_settings_dashboard.component';
import { LoginComponent } from './auth/login/login.component';
import { ProfileComponent } from './profile/profile.component';
import { SearchResultPageComponent } from './search-result-page/search-result-page.component';
// import { ManageUserComponent } from './user_settings/user_settings_dashboard/manage-user/manage-user.component';
// import { ManageAgentComponent } from './user_settings/user_settings_dashboard/manage-agent/manage-agent.component';
// import { GroupUserComponent } from './user_settings/user_settings_dashboard/group-user/group-user.component';
// import { ManagePermissionComponent } from './user_settings/user_settings_dashboard/manage-permissions/manage-permissions.component';
import { MessageService } from 'primeng/api';
import { AddMemberGroupComponent } from './add-member-group/add-member-group.component';
// import { ManageGroupPermissionComponent } from './user_settings/user_settings_dashboard/manage-group-permissions/manage-group-permissions.component';
// import { ManageCombosComponent } from './user_settings/user_settings_dashboard/manage-combos/manage-combos.component';
// import { ManageNoteTypesComponent } from './user_settings/user_settings_dashboard/manage-note-types/manage-note-types.component';
// import { ManageComboItemsComponent } from './user_settings/user_settings_dashboard/manage-combo-items/manage-combo-items.component';
// import { ManageMakeModelsComponent } from './user_settings/user_settings_dashboard/manage-make-models/manage-make-models.component';
// import { ImageModalComponent } from './user_settings/user_settings_dashboard/manage-combo-items/image-modal/image-modal.component';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { AgGridModule } from 'ag-grid-angular';
import { LoaderInterceptor } from './shared/services/loader.interceptor';
import { ClientConfigInterceptor } from './shared/services/client-config.interceptor';
import { ApiUnauthorisedInterceptor } from './shared/services/api-unauthorised.interceptor';
import { PaymentComponentsModule } from './shared/payment-components/payment-components.module';
// import { ManageResourcesComponent } from './user_settings/user_settings_dashboard/manage-resources/manage-resources.component';
// import { ResourceTypeModalComponent } from './user_settings/user_settings_dashboard/resource-type-modal/resource-type-modal.component';
// import { ManageCategoryComponent } from './user_settings/user_settings_dashboard/manage-category/manage-category.component';
// import { NewManageUserComponent } from './user_settings/user_settings_dashboard/new-manage-user/new-manage-user.component';
// import { EditManageUsersComponent } from './user_settings/user_settings_dashboard/edit-manage-users/edit-manage-users.component';
// import { NewProfileComponent } from './user_settings/user_settings_dashboard/new-profile/new-profile.component';
// import { NewManageAngentComponent } from './user_settings/user_settings_dashboard/new-manage-angent/new-manage-angent.component';
// import { EditAddAgentComponent } from './user_settings/user_settings_dashboard/edit-add-agent/edit-add-agent.component';
import { NgxIndexedDBModule, DBConfig } from 'ngx-indexed-db';
import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { reducers } from './store/app.state';
// import { metaReducers } from './store/app.effect';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';
import { localStorageSync } from 'ngrx-store-localstorage';
import { EffectsModule } from '@ngrx/effects';
import { NotificationEffects } from './store/notifications/notification.effect';
// import { NewDashboardComponent } from './customer-dashboard/customer-pages/new-dashboard/new-dashboard.component';
// import { CustomerPagesModule } from './customer-dashboard/customer-pages/customer-pages.module';
import { RouteReuseStrategy } from '@angular/router';
import { CompositeReuseStrategy } from './shared/services/composite-reuse-strategy';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from "@sentry/angular";
import { PermissionResolver } from './shared/services/permission.resolver';
import { TokenInterceptor } from './shared/services/token.interceptor';
import { GlobalErrorHandlerService } from './shared/services/global-error-handler.service';
import { TraceService } from '@sentry/angular';
import { ToastrModule } from 'ngx-toastr';
import { StorageIntegrityService } from './shared/services/storage-integrity.service';
import { AuthStateService } from './shared/services/auth-state.service';
import { AuthService } from './auth/services/auth.service';
// import { SentryApiInterceptor } from './sentry-automation/interceptor/sentry-api-interceptor.service';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

/**
 * APP_INITIALIZER factory — runs BEFORE any route activates.
 *
 * Guarantees the access token is in-memory before the first authenticated
 * HTTP request fires, eliminating the page-refresh race condition where
 * API calls fired by child components would get 401 (no token yet) and
 * trigger a signout before the tokenRefresh response arrived.
 */
export function appInitializerFactory(
  storageIntegrity: StorageIntegrityService,
  authState: AuthStateService,
  authService: AuthService
): () => Promise<void> {
  return async () => {
    // Force-validate storage integrity (bypasses 10s cache)
    const isValid = await storageIntegrity.validateIntegrity(true);
    if (!isValid) {
      storageIntegrity.handleTampering();
      return;
    }

    // Populate companyTokens map (userId) so the refresh call has context
    authState.hydrateFromStorage();

    const hadPersistedSession =
      localStorage.getItem('isLoggedIn') === 'true' &&
      localStorage.getItem('isCustomerLogin') === 'true';

    // Rehydrate access token via tokenRefresh cookie — BLOCKS routing until done
    const authRestored = await authService.initializeAuthFromStorage();

    if (hadPersistedSession && !authRestored) {
      await authService.logout();
    }
  };
}

export function localStorageSyncReducer(reducer: any): any {
  return localStorageSync({ keys: ['notifications'], rehydrate: true })(reducer);
}
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];


const combinedDBConfig: DBConfig = {
  name: "CombinedDB", // New unified database name
  version: 3,
  objectStoresMeta: [
    {
      store: "resource_categories",
      storeConfig: { keyPath: "Name", autoIncrement: false },
      storeSchema: [],
    },
    {
      store: "unity_searches", // Store for Unity searches
      storeConfig: { keyPath: "id", autoIncrement: true },
      storeSchema: [
        {
          name: "searchTerm",
          keypath: "searchTerm",
          options: { unique: false },
        },
        { name: "activeTab", keypath: "activeTab", options: { unique: false } },
        { name: "items", keypath: "items", options: { unique: false } },
        { name: "timestamp", keypath: "timestamp", options: { unique: false } },
      ],
    },
    {
      store: "mingle_searches", // Store for Mingle searches
      storeConfig: { keyPath: "id", autoIncrement: true },
      storeSchema: [
        {
          name: "searchTerm",
          keypath: "searchTerm",
          options: { unique: false },
        },
        { name: "activeTab", keypath: "activeTab", options: { unique: false } },
        { name: "items", keypath: "items", options: { unique: false } },
        { name: "timestamp", keypath: "timestamp", options: { unique: false } },
      ],
    },
  ],
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ProfileComponent,
    AddMemberGroupComponent,
    SearchResultPageComponent,
    // NewDashboardComponent,
  ],
  imports: [
    AgGridModule,
    CommonModule,
    BrowserModule,
    FormsModule,
    CdkStepperModule,
    NgxWebstorageModule.forRoot({
      prefix: "tibss",
      separator: ".",
      caseSensitive: true,
    }),
    ReactiveFormsModule,
    AppRoutingModule,
    HttpClientModule,
    NgbModule,
    SharedModule,
    RecaptchaModule,
    NgbPaginationModule,
    RecaptchaFormsModule,
    CarouselModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    BsDatepickerModule.forRoot(),
    BrowserAnimationsModule,
    NgxIndexedDBModule.forRoot(combinedDBConfig),
    StoreModule.forRoot(reducers, { metaReducers }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production
    }),
    EffectsModule.forRoot([NotificationEffects]),
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      progressBar: true
    }),
    PaymentComponentsModule,
    // CustomerPagesModule,
  ],

  providers: [
    TraceService,
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: SentryApiInterceptor,
    //   multi: true,
    // },
    { provide: RouteReuseStrategy, useClass: CompositeReuseStrategy },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    MessageService,PermissionResolver,
    // Interceptor order (request direction → response direction reversed):
    // 1. ClientConfig  – adds X-Client-Code + withCredentials
    // 2. Token         – adds Bearer token, handles 401 + refresh
    // 3. Loader        – shows/hides loading spinner
    // 4. Unauthorised  – forces logout after failed refresh (post-retry 401 / 403)
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ClientConfigInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiUnauthorisedInterceptor,
      multi: true,
    },
    {
          provide: ErrorHandler,
          useClass: GlobalErrorHandlerService,
        },
        {
          provide: Sentry.TraceService,
          deps: [Router],
        },
        {
          provide: APP_INITIALIZER,
          useFactory: () => () => {},
          deps: [Sentry.TraceService],
          multi: true,
        },
        // Auth initializer — runs BEFORE routing, eliminates token race condition
        {
          provide: APP_INITIALIZER,
          useFactory: appInitializerFactory,
          deps: [StorageIntegrityService, AuthStateService, AuthService],
          multi: true,
        },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
